"use client";

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createProduct } from '@/lib/supabase/products';
import { createCollaborativeProductPoolItem } from '@/lib/supabase/productPool';
import { useSession } from '@/context/SessionContext';

export default function NewProductWizard({ 
  initialTitle, 
  initialBarcode, 
  onClose, 
  onCreated,
  storeId = null,
  locationName = null,
  latitude = null,
  longitude = null,
}:{
  initialTitle?: string;
  initialBarcode?: string;
  onClose: ()=>void;
  onCreated?: (entry:any)=>void;
  storeId?: string | null;
  locationName?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}){
  const { session } = useSession();
  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [contentQuantity, setContentQuantity] = useState('');
  const [contentUnit, setContentUnit] = useState<'gr' | 'ml'>('gr');
  const [stock, setStock] = useState('');
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-generate title from brand + model + content
  React.useEffect(() => {
    const parts = [brand, model, contentQuantity ? `${contentQuantity}${contentUnit}` : ''].filter(p => p?.trim());
    setTitle(parts.join(' ').trim());
  }, [brand, model, contentQuantity, contentUnit]);

  const handleCreate = async () => {
    if (!session?.user) return alert('Debés iniciar sesión para crear un producto');
    setLoading(true);
    try {
      // Build content string from quantity + unit if provided
      let contentStr = '';
      if (contentQuantity) {
        contentStr = `${contentQuantity}${contentUnit}`;
      }
      
      const finalTitle = title || initialTitle || brand || 'Producto';
      const priceNumber = price ? parseFloat(price) : 0;
      const stockNumber = stock ? Math.max(0, parseInt(stock, 10)) : 0;
      
      // 1. Crear en el Pool Colaborativo (collaborative_product_pool)
      const poolPayload = {
        barcode: initialBarcode || undefined,
        title: finalTitle,
        brand: brand || undefined,
        model: model || undefined,
        content: contentStr || undefined,
        stock: stockNumber,
        fixed_sale_price: priceNumber || undefined,
        product_type: 'groceries' as const,
        location_name: locationName || undefined,
        latitude: latitude || undefined,
        longitude: longitude || undefined,
      };
      
      const poolEntry = await createCollaborativeProductPoolItem(poolPayload, session.user.id);
      
      // 2. Crear en la tabla de productos (products) si hay storeId
      let productEntry = poolEntry; // default to pool entry
      
      if (storeId) {
        productEntry = await createProduct(
          session.user.id,
          finalTitle,
          null, // author
          null, // description
          null, // main_image_url
          null, // fixed_rental_price
          priceNumber || 0,
          false, // free_shipping
          false, // is_auction
          null, // auction_start_time
          null, // auction_end_time
          null, // starting_bid
          null, // buy_now_price
          'groceries', // product_type
          'new', // condition
          [], // image_urls
          null, // main_image_url
          'available', // status
          {
            barcode: initialBarcode || null,
            brand: brand || null,
            model: model || null,
            content: contentStr || null,
            quantity_kind: contentUnit === 'ml' ? 'liquid' : 'mass',
          }, // details
          null, // rental_options
          latitude || undefined, // latitude
          longitude || undefined, // longitude
          locationName || undefined, // location_name
          storeId // store_id
        );
      }
      
      if (onCreated) onCreated({
        title: finalTitle,
        brand: brand || null,
        model: model || null,
        content: contentStr || null,
        barcode: initialBarcode || null,
      });
      onClose();
    } catch (e) {
      console.error('NewProductWizard create error', e);
      const msg = (e as any)?.message || String(e);
      alert('No se pudo crear el producto: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded p-4 w-full max-w-xl">
        <h3 className="text-lg font-semibold">Crear nuevo producto</h3>
        <div className="mt-3 grid grid-cols-1 gap-2">
          <div className="grid gap-1">
            <label className="text-sm font-medium">Título (Auto-generado)</label>
            <Input 
              value={title} 
              disabled 
              placeholder="Se genera automáticamente desde marca + tipo + contenido"
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
          <Input value={brand} onChange={(e)=>setBrand(e.target.value)} placeholder="Marca" disabled={loading} />
          <Input value={model} onChange={(e)=>setModel(e.target.value)} placeholder="Modelo" disabled={loading} />
          
          <div className="grid gap-2">
            <label className="text-sm font-medium">Contenido</label>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label htmlFor="content-qty" className="text-xs mb-1 block">Cantidad</label>
                <Input 
                  id="content-qty"
                  type="number" 
                  step="0.1" 
                  min="0"
                  value={contentQuantity} 
                  onChange={(e)=>setContentQuantity(e.target.value)} 
                  placeholder="Ej: 500"
                  disabled={loading}
                />
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant={contentUnit === 'gr' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setContentUnit('gr')}
                  disabled={loading}
                >
                  gr
                </Button>
                <Button
                  type="button"
                  variant={contentUnit === 'ml' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setContentUnit('ml')}
                  disabled={loading}
                >
                  ml
                </Button>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Input 
              type="number" 
              placeholder="Stock" 
              value={stock} 
              onChange={(e) => setStock(e.target.value)}
              disabled={loading}
            />
            <Input 
              type="number" 
              placeholder="Precio" 
              value={price} 
              onChange={(e) => setPrice(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleCreate} disabled={loading || !title}>{loading ? 'Creando...' : 'Crear y marcar disponible'}</Button>
        </div>
      </div>
    </div>
  );
}
